import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import {
  Plus,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
  Building,
  Car,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb,
  PiggyBank,
  Package,
  FileText,
  X,
  ChevronUp,
  ChevronDown,
  History,
  Factory
} from "lucide-react";
import "./NetWorth.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://techiohisab.com/api";
const EMPTY_SUMMARY = {
  totalAssets: 0,
  totalLiabilities: 0,
  netWorth: 0,
  breakdown: [],
  additional: {},
  dashboardCards: [],
  businessIntelligence: null,
  historicalAnalytics: null
};
const MANUFACTURE_FILTERED_VALUE_STORAGE_KEY = "manufacture_filtered_value_total";
const MANUFACTURE_FILTERED_VALUE_EVENT = "manufacture-filtered-value-updated";
const PACKAGING_NET_WORTH_EVENT = "packaging-networth-updated";
const PACKAGING_NET_WORTH_STORAGE_KEY = "packaging_networth_updated_at";
const SALARY_NET_WORTH_EVENT = "salary-networth-updated";
const SALARY_NET_WORTH_STORAGE_KEY = "salary_networth_updated_at";

const readManufactureFilteredValue = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(MANUFACTURE_FILTERED_VALUE_STORAGE_KEY) || "{}");
    const value = Number(parsed?.value);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

export default function NetWorth() {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [manufactureFilteredValue, setManufactureFilteredValue] = useState(readManufactureFilteredValue);
  const [expenseLiability, setExpenseLiability] = useState(0);
  
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showBalanceAdjustModal, setShowBalanceAdjustModal] = useState(false);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedBusinessMonth, setSelectedBusinessMonth] = useState("");
  const isMountedRef = useRef(true);
  const summaryRequestRef = useRef(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    type: "ASSET",
    description: ""
  });
  
  const [accountForm, setAccountForm] = useState({
    category_id: "",
    account_name: "",
    opening_balance: ""
  });
  
  const [transactionForm, setTransactionForm] = useState({
    account_id: "",
    type: "INCREASE",
    amount: "",
    note: "",
    transaction_date: new Date().toISOString().split('T')[0]
  });

  const [balanceAdjustForm, setBalanceAdjustForm] = useState({
    account_id: "",
    adjustment_type: "manual_add",
    amount: "",
    note: "",
    adjustment_date: new Date().toISOString().split('T')[0]
  });

  // Fetch data
  const fetchSummary = async () => {
    try {
      console.log("🔍 Fetching net worth summary...");
      const res = await axios.get(`${API_BASE}/networth/summary`);
      console.log("✅ Summary data:", res.data);
      if (!isMountedRef.current) return;
      setSummary(res.data);
    } catch (error) {
      console.error("❌ Error fetching summary:", error.response?.data || error.message);
      // Set default values on error
      if (!isMountedRef.current) return;
      setSummary(EMPTY_SUMMARY);
    }
  };

  const refreshSummary = useCallback(async ({ force = false } = {}) => {
    // Safe optimization: reuse the current summary request unless a post-mutation refresh needs the latest backend value.
    if (summaryRequestRef.current && !force) {
      return summaryRequestRef.current;
    }

    if (summaryRequestRef.current && force) {
      try {
        await summaryRequestRef.current;
      } catch {
        // Ignore the previous request result and continue with a fresh fetch.
      }
    }

    const request = fetchSummary()
      .catch((error) => {
        if (!isMountedRef.current) return;
        console.error("Error refreshing summary:", error?.response?.data || error?.message || error);
        setSummary(EMPTY_SUMMARY);
      })
      .finally(() => {
        if (summaryRequestRef.current === request) {
          summaryRequestRef.current = null;
        }
      });

    summaryRequestRef.current = request;
    return request;
  }, []);

  const fetchCategories = async () => {
    try {
      console.log("🔍 Fetching categories...");
      const res = await axios.get(`${API_BASE}/networth/categories`);
      console.log("✅ Categories data:", res.data);
      setCategories(res.data);
    } catch (error) {
      console.error("❌ Error fetching categories:", error.response?.data || error.message);
      setCategories([]);
    }
  };

  const fetchAccounts = async () => {
    try {
      console.log("🔍 Fetching accounts...");
      const res = await axios.get(`${API_BASE}/networth/accounts`);
      console.log("✅ Accounts data:", res.data);
      setAccounts(res.data);
    } catch (error) {
      console.error("❌ Error fetching accounts:", error.response?.data || error.message);
      setAccounts([]);
    }
  };

  const fetchBalanceHistory = async () => {
    try {
      console.log("🔍 Fetching balance history...");
      const res = await axios.get(`${API_BASE}/networth/balance/history`);
      console.log("✅ Balance history data:", res.data);
      setBalanceHistory(res.data || []);
    } catch (err) {
      console.error("❌ Failed to load balance history:", err);
      setBalanceHistory([]);
    }
  };

  const fetchExpenseLiability = async () => {
    try {
      const res = await axios.get(`${API_BASE}/expenses/summary`);
      const value = Number(res.data?.totalExpenses || 0);
      if (!isMountedRef.current) return;
      setExpenseLiability(Number.isFinite(value) ? value : 0);
    } catch (error) {
      console.error("Failed to load expense liability:", error?.response?.data || error?.message);
      if (!isMountedRef.current) return;
      setExpenseLiability(0);
    }
  };

  const fetchTransactions = async (accountId) => {
    try {
      const res = await axios.get(`${API_BASE}/networth/transactions/${accountId}`);
      setTransactions(res.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  useEffect(() => {
    // Safe cleanup: prevent state updates after unmount while summary refreshes are active.
    isMountedRef.current = true;

    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        await Promise.all([
          refreshSummary({ force: true }),
          fetchCategories(),
          fetchAccounts(),
          fetchBalanceHistory(),
          fetchExpenseLiability()
        ]);
      } catch (err) {
        setError("Failed to load Net Worth data");
        console.error("Initial data loading error:", err);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
    
    return () => {
      isMountedRef.current = false;
    };
  }, [refreshSummary]);

  useEffect(() => {
    // Safe optimization: poll summary every 10 seconds and clear the interval on unmount.
    const summaryInterval = window.setInterval(() => {
      refreshSummary();
      fetchExpenseLiability();
    }, 10000);

    return () => {
      window.clearInterval(summaryInterval);
    };
  }, [refreshSummary]);

  useEffect(() => {
    const refreshSalaryLiability = () => refreshSummary({ force: true });
    const handleSalaryStorage = (event) => {
      if (event.key === SALARY_NET_WORTH_STORAGE_KEY) refreshSalaryLiability();
    };

    window.addEventListener(SALARY_NET_WORTH_EVENT, refreshSalaryLiability);
    window.addEventListener("storage", handleSalaryStorage);
    return () => {
      window.removeEventListener(SALARY_NET_WORTH_EVENT, refreshSalaryLiability);
      window.removeEventListener("storage", handleSalaryStorage);
    };
  }, [refreshSummary]);

  useEffect(() => {
    const refreshPackagingMaterialValue = () => refreshSummary({ force: true });
    const handlePackagingStorage = (event) => {
      if (event.key === PACKAGING_NET_WORTH_STORAGE_KEY) refreshPackagingMaterialValue();
    };

    window.addEventListener(PACKAGING_NET_WORTH_EVENT, refreshPackagingMaterialValue);
    window.addEventListener("storage", handlePackagingStorage);
    return () => {
      window.removeEventListener(PACKAGING_NET_WORTH_EVENT, refreshPackagingMaterialValue);
      window.removeEventListener("storage", handlePackagingStorage);
    };
  }, [refreshSummary]);

  useEffect(() => {
    const refreshOpenSalesImpact = () => refreshSummary({ force: true });
    window.addEventListener("networth-refresh", refreshOpenSalesImpact);
    return () => window.removeEventListener("networth-refresh", refreshOpenSalesImpact);
  }, [refreshSummary]);

  useEffect(() => {
    const updateManufactureFilteredValue = (event) => {
      const nextValue = Number(event?.detail?.value);
      setManufactureFilteredValue(Number.isFinite(nextValue) ? nextValue : readManufactureFilteredValue());
    };

    const handleStorage = (event) => {
      if (event.key === MANUFACTURE_FILTERED_VALUE_STORAGE_KEY) {
        setManufactureFilteredValue(readManufactureFilteredValue());
      }
    };

    window.addEventListener(MANUFACTURE_FILTERED_VALUE_EVENT, updateManufactureFilteredValue);
    window.addEventListener("storage", handleStorage);
    setManufactureFilteredValue(readManufactureFilteredValue());

    return () => {
      window.removeEventListener(MANUFACTURE_FILTERED_VALUE_EVENT, updateManufactureFilteredValue);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (showCategoryModal || showAccountModal) {
      document.body.style.overflow = "hidden";
      document.body.classList.add("modal-open");
    } else {
      document.body.style.overflow = "auto";
      document.body.classList.remove("modal-open");
    }
  }, [showCategoryModal, showAccountModal]);

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    console.log("🔥 Frontend handleCategorySubmit called with:", categoryForm);
    setLoading(true);
    
    try {
      let response;
      if (editingCategory) {
        console.log("🔄 Updating category:", editingCategory.id);
        response = await axios.put(`${API_BASE}/networth/category/update/${editingCategory.id}`, categoryForm);
      } else {
        console.log("➕ Creating new category");
        response = await axios.post(`${API_BASE}/networth/category/create`, categoryForm);
      }
      
      console.log("✅ Category API response:", response.data);
      await Promise.all([
        fetchCategories(),
        fetchAccounts(),
        refreshSummary({ force: true }),
        fetchBalanceHistory()
      ]);
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", type: "ASSET", description: "" });
    } catch (error) {
      console.error("❌ Error saving category:", error.response?.data || error.message);
      alert(`Failed to save category: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    if (!category) return;
    setEditingCategory(category);
    setCategoryForm({
      name: category?.name || '',
      type: category?.type || 'ASSET',
      description: category?.description || ''
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this category and all its accounts?")) return;
    
    try {
      await axios.delete(`${API_BASE}/networth/category/delete/${id}`);
      await Promise.all([
        fetchCategories(),
        fetchAccounts(),
        refreshSummary({ force: true }),
        fetchBalanceHistory()
      ]);
    } catch (error) {
      console.error("Error deleting category:", error);
      alert("Failed to delete category");
    }
  };

  // Account handlers
  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    console.log("🔥 Frontend handleAccountSubmit called with:", accountForm);
    setLoading(true);
    
    try {
      let response;
      if (editingAccount) {
        console.log("🔄 Updating account:", editingAccount.id);
        response = await axios.put(`${API_BASE}/networth/account/update/${editingAccount.id}`, accountForm);
      } else {
        console.log("➕ Creating new account");
        response = await axios.post(`${API_BASE}/networth/account/create`, accountForm);
      }
      
      console.log("✅ Account API response:", response.data);
      await Promise.all([
        fetchAccounts(),
        fetchCategories(),
        refreshSummary({ force: true }),
        fetchBalanceHistory()
      ]);
      setShowAccountModal(false);
      setEditingAccount(null);
      setAccountForm({ category_id: "", account_name: "", opening_balance: "" });
    } catch (error) {
      console.error("❌ Error saving account:", error.response?.data || error.message);
      alert(`Failed to save account: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAccount = (account) => {
    if (!account) return;
    setEditingAccount(account);
    setAccountForm({
      category_id: account?.category_id || '',
      account_name: account?.account_name || '',
      opening_balance: account?.opening_balance || 0
    });
    setShowAccountModal(true);
  };

  const handleDeleteAccount = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this account and all its transactions?")) return;
    
    try {
      await axios.delete(`${API_BASE}/networth/account/delete/${id}`);
      await Promise.all([
        fetchAccounts(),
        refreshSummary({ force: true }),
        fetchBalanceHistory()
      ]);
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account");
    }
  };

  // Transaction handlers
  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_BASE}/networth/transaction/add`, transactionForm);
      
      await Promise.all([
        fetchAccounts(),
        fetchCategories(),
        refreshSummary({ force: true }),
        fetchBalanceHistory()
      ]);
      if (selectedAccount) {
        fetchTransactions(selectedAccount.id);
      }
      
      setShowTransactionModal(false);
      setTransactionForm({
        account_id: "",
        type: "INCREASE",
        amount: "",
        note: "",
        transaction_date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error("Error adding transaction:", error);
      alert("Failed to add transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = (account) => {
    if (!account || !account?.id) return;
    setSelectedAccount(account);
    setShowHistoryModal(true);
    fetchTransactions(account.id);
  };

  // Balance adjustment handlers
  const handleBalanceAdjust = (account) => {
    if (!account || !account?.id) return;
    setSelectedAccount(account);
    setBalanceAdjustForm({
      account_id: account.id,
      adjustment_type: "manual_add",
      amount: "",
      note: "",
      adjustment_date: new Date().toISOString().split('T')[0]
    });
    setShowBalanceAdjustModal(true);
  };

  const handleBalanceAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!balanceAdjustForm.amount || !balanceAdjustForm.note) {
      alert("Amount and note are required");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/networth/balance/`, balanceAdjustForm);
      
      if (response.data.success) {
        // Refresh all data
        await Promise.all([
          fetchAccounts(),
          fetchCategories(),
          refreshSummary({ force: true }),
          fetchBalanceHistory()
        ]);
        
        // Close modal
        setShowBalanceAdjustModal(false);
        setBalanceAdjustForm({
          account_id: "",
          adjustment_type: "manual_add",
          amount: "",
          note: "",
          adjustment_date: new Date().toISOString().split('T')[0]
        });
        
        alert("Balance adjusted successfully!");
      }
    } catch (error) {
      console.error("Error adjusting balance:", error);
      alert(`Failed to adjust balance: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBalanceHistory = (account) => {
    if (!account || !account?.id) return;
    // Navigate to balance history page for specific account
    window.location.href = `/networth/balance-history?account_id=${account.id}`;
  };

  // Icon mapping
  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return <DollarSign className="w-5 h-5" />;
    const name = (categoryName || '').toLowerCase();
    if (name.includes('cash') || name.includes('bank')) return <DollarSign className="w-5 h-5" />;
    if (name.includes('investment')) return <TrendingUp className="w-5 h-5" />;
    if (name.includes('real estate') || name.includes('property')) return <Building className="w-5 h-5" />;
    if (name.includes('vehicle') || name.includes('car') || name.includes('bike')) return <Car className="w-5 h-5" />;
    if (name.includes('loan') || name.includes('credit')) return <CreditCard className="w-5 h-5" />;
    return <PiggyBank className="w-5 h-5" />;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getBreakdownCards = (currentSummary) => {
    const baseCards = Array.isArray(currentSummary?.dashboardCards) && currentSummary.dashboardCards.length > 0
      ? [...currentSummary.dashboardCards]
      : [
          {
            id: "inventoryValue",
            label: "Inventory Value",
            value: currentSummary?.additional?.inventoryValue || 0,
            tone: "asset",
            note: "Live stock valued from the inventory table",
            hideWhenZero: false
          },
          {
            id: "openSalesValue",
            label: "Open Sales Value",
            value: currentSummary?.additional?.outstandingSales || 0,
            tone: "asset",
            note: "Non-cancelled sales that still affect the live business picture",
            hideWhenZero: false
          },
          {
            id: "purchaseLiability",
            label: "Purchase Liability",
            value: currentSummary?.additional?.outstandingPurchases || 0,
            tone: "liability",
            note: "Accepted purchase exposure from the purchases module",
            hideWhenZero: false
          },
          {
            id: "salaryLiability",
            label: "Salary Liability",
            value: currentSummary?.additional?.outstandingSalaries || 0,
            tone: "liability",
            note: "Unsettled salary rows from the payroll flow",
            hideWhenZero: false
          },
          {
            id: "openReturnAmount",
            label: "Open Return",
            value: currentSummary?.additional?.openReturnAmount || 0,
            tone: "asset",
            note: "Live RTO/DTO amount from Return in Transit",
            hideWhenZero: false
          },
          {
            id: "settlementReceived",
            label: "Settlement Received",
            value: currentSummary?.additional?.settlementReceived || 0,
            tone: "asset",
            note: "Total settlement received through bank reports",
            hideWhenZero: false
          },
          {
            id: "expenseLiability",
            label: "Expense Liabilities",
            value: currentSummary?.additional?.expenseLiability || 0,
            tone: "liability",
            note: "Live total expenses from the expense module",
            hideWhenZero: false
          },
          {
            id: "rtoLosses",
            label: "RTO Loss",
            value: currentSummary?.additional?.rtoLosses || 0,
            tone: "liability",
            note: "RTO-only loss exposure from the returns flow",
            hideWhenZero: false
          },
          {
            id: "dtoLosses",
            label: "DTO Loss",
            value: currentSummary?.additional?.dtoLosses || 0,
            tone: "liability",
            note: "DTO-only loss exposure from the returns flow",
            hideWhenZero: false
          },
          {
            id: "settlementExposure",
            label: "Settlement Exposure",
            value: currentSummary?.additional?.settlementExposure || 0,
            tone: "info",
            note: "Tracked separately to avoid double counting in net worth",
            hideWhenZero: true
          }
        ];

    const existingIds = new Set(baseCards.map(card => card.id));

    const additionalCards = [
      {
        id: "expenseLiability",
        label: "Expense Liabilities",
        value: currentSummary?.additional?.expenseLiability ?? expenseLiability,
        tone: "liability",
        note: "Live total expenses from the expense module",
        hideWhenZero: false
      },
      {
        id: "rtoScannedValue",
        label: "RTO Scanned Value",
        value: currentSummary?.additional?.rtoScannedValue || 0,
        tone: "asset",
        note: "Scanned value from RTO items",
        hideWhenZero: false
      },
      {
        id: "manufacturingUnitsValue",
        label: "Manufacturing Units Value",
        value:
          manufactureFilteredValue !== null
            ? manufactureFilteredValue
            : currentSummary?.additional?.manufacturingUnitsValue || 0,
        tone: "asset",
        note: "Live filtered value from manufacturing units",
        hideWhenZero: false
      },
      {
        id: "manufacturingLoss",
        label: "Manufacturing Loss",
        value: currentSummary?.additional?.manufacturingLoss || 0,
        tone: "liability",
        note: "Loss from manufacturing items (costs exceeding final value)",
        hideWhenZero: false
      }
    ];

    const finalCards = [...baseCards, ...additionalCards.filter(card => !existingIds.has(card.id))];

    return finalCards.filter((card) => !card.hideWhenZero || Number(card.value || 0) !== 0);
  };

  const backendIncludesExpenseLiability =
    Object.prototype.hasOwnProperty.call(summary?.additional || {}, "expenseLiability") ||
    (Array.isArray(summary?.dashboardCards) &&
      summary.dashboardCards.some((card) => card?.id === "expenseLiability"));
  const displayedTotalLiabilities =
    Number(summary?.totalLiabilities || 0) +
    (backendIncludesExpenseLiability ? 0 : Number(expenseLiability || 0));
  const displayedNetWorth = Number(summary?.totalAssets || 0) - displayedTotalLiabilities;

  const getBreakdownIcon = (cardId) => {
    switch (cardId) {
      case "inventoryValue":
        return <Package className="w-5 h-5" />;
      case "openSalesValue":
        return <FileText className="w-5 h-5" />;
      case "openReturnAmount":
        return <Car className="w-5 h-5" />;
      case "settlementReceived":
        return <Building className="w-5 h-5" />;
      case "purchaseLiability":
        return <CreditCard className="w-5 h-5" />;
      case "salaryLiability":
      case "expenseLiability":
        return <DollarSign className="w-5 h-5" />;
      case "rtoLosses":
      case "dtoLosses":
      case "rtoScannedValue":
        return <Car className="w-5 h-5" />;
      case "manufacturingUnitsValue":
      case "manufacturingLoss":
        return <Factory className="w-5 h-5" />;
      case "settlementExposure":
        return <Building className="w-5 h-5" />;
      default:
        return <PiggyBank className="w-5 h-5" />;
    }
  };

  const formatSignedCurrency = (amount) => {
    const value = Number(amount || 0);
    const absolute = formatCurrency(Math.abs(value));
    return value < 0 ? `-${absolute}` : absolute;
  };

  const formatPercentChange = (value) => {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
    const numeric = Number(value);
    const rounded = Math.abs(numeric).toFixed(1).replace(/\.0$/, "");
    return `${numeric >= 0 ? "+" : "-"}${rounded}%`;
  };

  const getBusinessCardIcon = (cardId, direction) => {
    switch (cardId) {
      case "monthlyProfitLoss":
        return direction === "up" ? (
          <ArrowUpRight className="w-5 h-5" />
        ) : direction === "down" ? (
          <ArrowDownRight className="w-5 h-5" />
        ) : (
          <DollarSign className="w-5 h-5" />
        );
      case "blockedExposure":
        return <AlertTriangle className="w-5 h-5" />;
      case "topLossArea":
        return <Car className="w-5 h-5" />;
      case "trend":
        return direction === "down" ? (
          <ArrowDownRight className="w-5 h-5" />
        ) : (
          <ArrowUpRight className="w-5 h-5" />
        );
      case "forecast":
        return <TrendingUp className="w-5 h-5" />;
      default:
        return <Lightbulb className="w-5 h-5" />;
    }
  };

  const getBusinessTone = (cardId, value, direction) => {
    if (cardId === "monthlyProfitLoss") {
      return Number(value || 0) >= 0 ? "asset" : "liability";
    }
    if (cardId === "forecast") {
      if (direction === "up") return "asset";
      if (direction === "down") return "liability";
      return "info";
    }
    if (cardId === "blockedExposure" || cardId === "topLossArea") {
      return "liability";
    }
    return "info";
  };

  const getHistoricalAnalytics = (currentSummary) => currentSummary?.historicalAnalytics || null;

  const getMonthTone = (month) => {
    if (!month?.hasData) return "info";
    if (month?.profitLoss >= 0) return "asset";
    if (month?.profitLoss < 0) return "liability";
    return "info";
  };

  const getCoverageLabel = (coverage) => {
    if (coverage === "strong") return "Strong data";
    if (coverage === "partial") return "Partial data";
    return "No activity";
  };

  const formatTrendPercent = (value) => {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
    const numeric = Number(value);
    const rounded = Math.abs(numeric).toFixed(1).replace(/\.0$/, "");
    return `${numeric >= 0 ? "+" : "-"}${rounded}%`;
  };

  const historicalAnalytics = getHistoricalAnalytics(summary);
  const historicalMonths = Array.isArray(historicalAnalytics?.monthHistory)
    ? historicalAnalytics.monthHistory
    : Array.isArray(historicalAnalytics?.months)
    ? historicalAnalytics.months
    : [];
  const availableBusinessMonths = Array.isArray(historicalAnalytics?.availableMonths)
    ? historicalAnalytics.availableMonths
    : Array.isArray(historicalAnalytics?.historyOptions)
    ? historicalAnalytics.historyOptions
    : historicalMonths.filter((month) => month?.hasData);
  const moneyBlockedItems = Array.isArray(historicalAnalytics?.moneyBlockedBreakdown?.items)
    ? historicalAnalytics.moneyBlockedBreakdown.items
    : [];
  const healthSignals = Array.isArray(historicalAnalytics?.healthSignals)
    ? historicalAnalytics.healthSignals
    : [];
  const historicalSummary = historicalAnalytics?.trendSummary || null;

  useEffect(() => {
    if (availableBusinessMonths.length === 0) {
      if (selectedBusinessMonth) {
        setSelectedBusinessMonth("");
      }
      return;
    }

    const latestMonthKey =
      availableBusinessMonths[0]?.monthKey ||
      availableBusinessMonths[0]?.value ||
      "";
    const isValidSelection = availableBusinessMonths.some((month) => {
      const monthKey = month?.monthKey || month?.value || "";
      return monthKey === selectedBusinessMonth;
    });

    if (!selectedBusinessMonth) {
      setSelectedBusinessMonth(latestMonthKey);
      return;
    }

    if (!isValidSelection && latestMonthKey) {
      setSelectedBusinessMonth(latestMonthKey);
    }
  }, [availableBusinessMonths, selectedBusinessMonth]);

  const selectedBusinessMonthData =
    historicalMonths.find((month) => month.monthKey === selectedBusinessMonth) ||
    historicalMonths[historicalMonths.length - 1] ||
    null;
  const previousSelectedBusinessMonth =
    historicalMonths.findIndex((month) => month.monthKey === selectedBusinessMonth) > 0
      ? historicalMonths[historicalMonths.findIndex((month) => month.monthKey === selectedBusinessMonth) - 1]
      : null;

  const selectedMonthLabel = selectedBusinessMonthData?.label || "Selected month";
  const selectedMonthTrendLabel =
    !selectedBusinessMonthData?.hasData
      ? "No data"
      : selectedBusinessMonthData?.direction === "up"
      ? "Improving"
      : selectedBusinessMonthData?.direction === "down"
      ? "Weaker"
      : "Stable";
  const selectedMonthTrendTone = getMonthTone(selectedBusinessMonthData);
  const selectedMonthWarning = selectedBusinessMonthData
    ? selectedBusinessMonthData.hasData
      ? selectedBusinessMonthData.rtoLoss > 0 &&
        selectedBusinessMonthData.revenue > 0 &&
        selectedBusinessMonthData.rtoLoss >= selectedBusinessMonthData.revenue * 0.15
        ? "RTO losses were high this month."
        : selectedBusinessMonthData.acceptedPurchaseSpend > 0 &&
          selectedBusinessMonthData.revenue > 0 &&
          selectedBusinessMonthData.acceptedPurchaseSpend > selectedBusinessMonthData.revenue
        ? "Inventory purchases were higher than sales."
        : selectedBusinessMonthData.topLossArea?.label === "Salary Burden"
        ? "Salary burden created pressure."
        : selectedBusinessMonthData.topLossArea?.label === "Purchase Expense"
        ? "Purchase pressure weighed on margins."
        : selectedBusinessMonthData.topLossArea?.label === "Packaging Overhead"
        ? "Packaging overhead was elevated."
        : selectedBusinessMonthData.topLossArea?.label === "RTO Impact"
        ? "Returned orders were the main drag."
        : null
      : "Not enough data for this month."
    : "Not enough data for this month.";

  const selectedMonthInsight = selectedBusinessMonthData
    ? selectedBusinessMonthData.hasData
      ? selectedBusinessMonthData.reason
      : "Not enough data for this month."
    : "Not enough data for this month.";

  const selectedMonthCards = selectedBusinessMonthData
    ? [
        {
          id: "monthlyProfitLoss",
          label: "Monthly Profit / Loss",
          value: selectedBusinessMonthData.profitLoss || 0,
          note: selectedBusinessMonthData.hasData
            ? `Compared with ${previousSelectedBusinessMonth?.label || "the previous month"}.`
            : "Not enough data for this month.",
          tone: getBusinessTone(
            "monthlyProfitLoss",
            selectedBusinessMonthData.profitLoss || 0,
            selectedBusinessMonthData.direction
          ),
          direction: selectedBusinessMonthData.direction || "flat",
          metric: formatPercentChange(selectedBusinessMonthData.percentChange),
          badge: selectedMonthTrendLabel,
        },
        {
          id: "topLossArea",
          label: "Biggest Pressure",
          value: selectedBusinessMonthData.topLossArea?.amount || 0,
          note: selectedBusinessMonthData.hasData
            ? `${selectedBusinessMonthData.topLossArea?.label || "Stable"} was the main pressure point.`
            : "Not enough data for this month.",
          tone: getBusinessTone("topLossArea"),
          subtitle: selectedBusinessMonthData.topLossArea?.label || "Stable",
          badge: "Main pressure",
        },
        {
          id: "trend",
          label: "Trend Movement",
          value: selectedBusinessMonthData.delta || 0,
          note: selectedBusinessMonthData.hasData
            ? "Movement versus the previous month."
            : "Not enough data for this month.",
          tone: selectedMonthTrendTone,
          direction: selectedBusinessMonthData.direction || "flat",
          metric: formatPercentChange(selectedBusinessMonthData.percentChange),
          badge:
            selectedBusinessMonthData.direction === "up"
              ? "Improving"
              : selectedBusinessMonthData.direction === "down"
              ? "Weaker"
              : "Stable",
        },
      ]
    : [];

  const selectedMonthPressureItems = selectedBusinessMonthData
    ? [
        {
          id: "rtoLoss",
          label: "RTO / DTO Loss",
          amount: selectedBusinessMonthData.rtoLoss || 0,
        },
        {
          id: "acceptedPurchaseSpend",
          label: "Inventory Purchases",
          amount: selectedBusinessMonthData.acceptedPurchaseSpend || 0,
        },
        {
          id: "salaryExpense",
          label: "Salary Burden",
          amount: selectedBusinessMonthData.salaryExpense || 0,
        },
        {
          id: "purchaseExpense",
          label: "Purchase Expense",
          amount: selectedBusinessMonthData.purchaseExpense || 0,
        },
        {
          id: "packagingExpense",
          label: "Packaging Overhead",
          amount: selectedBusinessMonthData.packagingExpense || 0,
        },
      ].filter((item) => Number(item.amount || 0) > 0)
    : [];

  return (
    <div className="networth-container">
      {/* Loading State */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner"></div>
            <p>Loading Net Worth data...</p>
          </div>
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div className="error-overlay">
          <div className="error-content">
            <h3>⚠️ Error</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="btn btn-primary">
              Reload Page
            </button>
          </div>
        </div>
      )}
      
      <div className="networth-content">
        {/* Header */}
        <div className="networth-header">
          <h2>Net Worth Management</h2>
          <div className="header-actions">
            <button className="btn btn-primary" onClick={() => setShowCategoryModal(true)}>
              <Plus className="w-4 h-4" /> Add Category
            </button>
            <button className="btn btn-primary" onClick={() => setShowAccountModal(true)}>
              <Plus className="w-4 h-4" /> Add Account
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => window.location.href = '/networth/balance-history'}
            >
              <History className="w-4 h-4" /> Balance History
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card assets">
            <div className="card-icon">
              <TrendingUp className="w-8 h-8" />
            </div>
            <div className="card-content">
              <h3>Total Assets</h3>
              <p className="amount">{formatCurrency(summary?.totalAssets || 0)}</p>
            </div>
          </div>
          
          <div className="summary-card liabilities">
            <div className="card-icon">
              <TrendingDown className="w-8 h-8" />
            </div>
            <div className="card-content">
              <h3>Total Liabilities</h3>
              <p className="amount">{formatCurrency(displayedTotalLiabilities)}</p>
            </div>
          </div>
          
          <div className={`summary-card ${displayedNetWorth >= 0 ? 'positive' : 'negative'}`}>
            <div className="card-icon">
              <DollarSign className="w-8 h-8" />
            </div>
            <div className="card-content">
              <h3>Net Worth</h3>
              <p className="amount">{formatCurrency(displayedNetWorth)}</p>
            </div>
          </div>
        </div>

        {/* Financial Breakdown */}
        <div className="section breakdown-section">
          <div className="section-heading">
            <div>
              <h3>Live Financial Breakdown</h3>
              <p>Module-linked balances synced from the centralized NetWorth engine.</p>
            </div>
            <span className="sync-badge">Auto-sync</span>
          </div>

          {(() => {
            const cards = getBreakdownCards(summary);
            const assetCards = cards.filter((card) => card.tone === "asset" || card.tone === "info");
            const liabilityCards = cards.filter((card) => card.tone === "liability");
            const destinations = { inventoryValue: "/inventory", openSalesValue: "/sales", openReturnAmount: "/return-in-transit", settlementReceived: "/reports", purchaseLiability: "/purchases", salaryLiability: "/salary", expenseLiability: "/expense-management", rtoLosses: "/rto", dtoLosses: "/rto", rtoScannedValue: "/rto", manufacturingUnitsValue: "/manufacture", manufacturingLoss: "/manufacture", settlementExposure: "/reports", packagingMaterialValue: "/packaging" };
            const renderCard = (card) => (
              <button key={card.id} type="button" onClick={() => { const destination = destinations[card.id]; if (destination) window.location.href = destination; }} className={`breakdown-card ${card.tone || "info"}`}>
                <div className="breakdown-card-top">
                  <div className="breakdown-icon">
                    {getBreakdownIcon(card.id)}
                  </div>
                  <span className={`breakdown-pill ${card.tone || "info"}`}>
                    {card.tone === "asset" ? "Asset" : card.tone === "liability" ? "Liability" : "Tracked"}
                  </span>
                </div>
                <div className="breakdown-card-body">
                  <h4>{card.label}</h4>
                  <p className="amount">{formatCurrency(card.value || 0)}</p>
                  <span className="breakdown-note">{card.note}</span>
                </div>
              </button>
            );
            return <div className="breakdown-groups"><div><h4 className="breakdown-group-title">Assets</h4><div className="breakdown-grid">{assetCards.map(renderCard)}</div></div><div><h4 className="breakdown-group-title">Liabilities</h4><div className="breakdown-grid">{liabilityCards.map(renderCard)}</div></div></div>;
          })()}
        </div>

        {/* Business Intelligence */}
        <div className="section intelligence-section">
          <div className="section-heading">
            <div>
              <h3>Business Intelligence</h3>
              <p>Choose a month to inspect that period only. Live totals stay unchanged.</p>
            </div>
            <div className="bi-filter">
              <label htmlFor="business-month-select">Month</label>
              <select
                id="business-month-select"
                value={selectedBusinessMonth || ""}
                onChange={(e) => setSelectedBusinessMonth(e.target.value)}
              >
                {availableBusinessMonths.length > 0 ? (
                  availableBusinessMonths.map((month) => {
                    const monthKey = month?.monthKey || month?.value || "";
                    const monthLabel = month?.label || month?.name || monthKey || "Unknown month";

                    return (
                      <option key={monthKey} value={monthKey}>
                        {monthLabel}
                      </option>
                    );
                  })
                ) : (
                  <option value="">Not enough historical data available</option>
                )}
              </select>
              {availableBusinessMonths.length === 0 && (
                <span className="bi-context-note subtle">Not enough historical data available</span>
              )}
            </div>
          </div>

          {selectedBusinessMonthData ? (
            <>
              <div className="bi-context-row">
                <span className={`sync-badge intelligence ${selectedMonthTrendTone}`}>
                  {selectedMonthTrendLabel}
                </span>
                <span className="bi-context-note">{selectedMonthLabel}</span>
                {selectedBusinessMonthData.isCurrentMonth && (
                  <span className="bi-context-note subtle">Month to date</span>
                )}
              </div>

              <div className="intelligence-grid">
                {selectedMonthCards.map((card) => (
                  <div key={card.id} className={`intelligence-card ${card.tone || "info"}`}>
                    <div className="intelligence-card-top">
                      <div className="intelligence-icon">
                        {getBusinessCardIcon(card.id, card.direction)}
                      </div>
                      <span className={`intelligence-pill ${card.tone || "info"}`}>
                        {card.badge || "Live"}
                      </span>
                    </div>
                    <div className="intelligence-card-body">
                      <h4>{card.label}</h4>
                      <p className="amount">
                        {card.value === null || card.value === undefined || !Number.isFinite(Number(card.value))
                          ? "Insufficient data"
                          : formatSignedCurrency(card.value)}
                      </p>
                      {card.metric && card.metric !== "Insufficient data" && (
                        <span className="intelligence-metric">{card.metric} vs previous month</span>
                      )}
                      {card.metric === "Insufficient data" && (
                        <span className="intelligence-metric subtle">{card.metric}</span>
                      )}
                      {card.subtitle && <span className="intelligence-subtitle">{card.subtitle}</span>}
                      <span className="breakdown-note">{card.note}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="insights-panel">
                <div className="insights-header">
                  <Lightbulb className="w-4 h-4" />
                  <h4>{selectedMonthLabel} summary</h4>
                </div>
                <ul className="insights-list">
                  {selectedMonthInsight ? <li>{selectedMonthInsight}</li> : null}
                  {selectedMonthWarning && selectedMonthWarning !== selectedMonthInsight ? (
                    <li>{selectedMonthWarning}</li>
                  ) : null}
                  {selectedBusinessMonthData.topLossArea?.label ? (
                    <li>Biggest pressure came from {selectedBusinessMonthData.topLossArea.label.toLowerCase()}.</li>
                  ) : null}
                  {selectedBusinessMonthData.hasData && selectedBusinessMonthData.direction !== "flat" ? (
                    <li>
                      Trend movement was {selectedBusinessMonthData.direction === "up" ? "positive" : "negative"} versus the previous month.
                    </li>
                  ) : null}
                </ul>
              </div>

              <div className="pressure-panel">
                <div className="pressure-panel-header">
                  <h4>Operational pressure</h4>
                  <span>Selected month only</span>
                </div>
                <div className="blocked-list compact">
                  {selectedMonthPressureItems.length > 0 ? (
                    selectedMonthPressureItems.map((item) => (
                      <div key={item.id} className="blocked-item info">
                        <div>
                          <strong>{item.label}</strong>
                          <span>Operational pressure recorded for this month.</span>
                        </div>
                        <div className="blocked-value">
                          <p>{formatCurrency(item.amount || 0)}</p>
                          <span>MONTH VALUE</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state compact">Not enough data for this month.</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state compact">Not enough historical data available.</div>
          )}
        </div>

        {/* Monthly Financial History */}
        <div className="section history-section">
          <div className="section-heading">
            <div>
              <h3>Monthly Financial History</h3>
              <p>Real month-by-month profit and loss from centralized business data.</p>
            </div>
            <span className="sync-badge history">12-month view</span>
          </div>

          {historicalSummary && (
            <div className="history-strip">
              <div className="history-strip-item">
                <span>Profitable months</span>
                <strong>{historicalSummary.profitableMonths || 0}</strong>
              </div>
              <div className="history-strip-item">
                <span>Loss-heavy months</span>
                <strong>{historicalSummary.lossMonths || 0}</strong>
              </div>
              <div className="history-strip-item">
                <span>Trend</span>
                <strong>{(historicalSummary.overallTrend || "stable").replace(/^./, (ch) => ch.toUpperCase())}</strong>
              </div>
              <div className="history-strip-item">
                <span>Data coverage</span>
                <strong>{getCoverageLabel(historicalSummary.monthsWithData >= 3 ? "strong" : historicalSummary.monthsWithData > 0 ? "partial" : "empty")}</strong>
              </div>
            </div>
          )}

          {historicalAnalytics && !historicalAnalytics.hasReliableHistory && (
            <p className="history-warning">
              Historical data is still limited, so trend signals remain conservative.
            </p>
          )}

          <div className="history-grid">
            {historicalMonths.length > 0 ? (
              historicalMonths.map((month) => {
                const trendTone = getMonthTone(month);
                const trendLabel =
                  month.direction === "up"
                    ? "Improving"
                    : month.direction === "down"
                    ? "Weaker"
                    : "Flat";

                return (
                  <div key={month.monthKey} className={`history-card ${trendTone}`}>
                    <div className="history-card-top">
                      <div>
                        <h4>{month.label}</h4>
                        <span className="history-card-subtitle">
                          {month.isCurrentMonth ? "Month to date" : "Completed month"}
                        </span>
                      </div>
                      <div className={`history-pill ${trendTone}`}>
                        {month.hasData ? trendLabel : "No activity"}
                      </div>
                    </div>
                    <div className="history-card-body">
                      <p className="amount">
                        {month.hasData ? formatSignedCurrency(month.profitLoss || 0) : "Insufficient data"}
                      </p>
                      {month.hasData && month.direction !== "flat" && month.percentChange !== null && (
                        <span className="history-trend">
                          {formatTrendPercent(month.percentChange)} vs previous month
                        </span>
                      )}
                      <span className="history-reason">{month.reason || "No major signal detected."}</span>
                      <span className="history-impact">
                        Major impact: {month.topLossArea?.label || "Stable"}
                      </span>
                      <span className="history-coverage">{getCoverageLabel(month.dataCoverage)}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">Not enough monthly history to show a trend yet.</div>
            )}
          </div>

          <div className="history-panels">
            <div className="history-panel">
              <div className="history-panel-header">
                <h4>Money Blocked Breakdown</h4>
                <span className="history-panel-note">
                  Tracked separately to avoid double counting.
                </span>
              </div>
              <div className="blocked-list">
                {moneyBlockedItems.length > 0 ? (
                  moneyBlockedItems.map((item) => (
                    <div key={item.id} className={`blocked-item ${item.tone || "info"}`}>
                      <div>
                        <strong>{item.label}</strong>
                        <span>{item.note}</span>
                      </div>
                      <div className="blocked-value">
                        <p>{formatCurrency(item.amount || 0)}</p>
                        <span>{String(item.reliability || "medium").toUpperCase()} reliability</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state compact">No blocked exposure data available.</div>
                )}
              </div>
            </div>

            <div className="history-panel">
              <div className="history-panel-header">
                <h4>Business Health Signals</h4>
                <span className="history-panel-note">Short, conservative, data-driven alerts.</span>
              </div>
              <div className="signal-list">
                {healthSignals.length > 0 ? (
                  healthSignals.map((signal) => (
                    <div key={signal.id} className={`signal-item ${signal.tone || "info"}`}>
                      <div className={`signal-badge ${signal.tone || "info"}`}>{signal.label}</div>
                      <span>{signal.note}</span>
                    </div>
                  ))
                ) : (
                  <div className="empty-state compact">No strong health signals detected.</div>
                )}
              </div>
              {historicalAnalytics?.insights?.length > 0 && (
                <div className="history-insights">
                  <h5>Historical insights</h5>
                  <ul>
                    {historicalAnalytics.insights.map((item, index) => (
                      <li key={`${index}-${item}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="section">
          <h3>Categories</h3>
          <div className="categories-grid">
            {(categories || []).map((category) => (
              <div key={category?.id || Math.random()} className={`category-card ${(category?.type || '').toLowerCase()}`}>
                <div className="category-header">
                  <div className="category-info">
                    <div className="category-icon">
                      {getCategoryIcon(category?.name || '')}
                    </div>
                    <div>
                      <h4>{category?.name || 'Unknown'}</h4>
                      <span className="category-type">{category?.type || 'UNKNOWN'}</span>
                    </div>
                  </div>
                  <div className="category-actions">
                    <button className="btn-icon" onClick={() => handleEditCategory(category)}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button className="btn-icon" onClick={() => handleDeleteCategory(category?.id)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="category-stats">
                  <span>{(accounts || []).filter(a => a?.category_id === category?.id).length} accounts</span>
                  <span>
                    {formatCurrency(
                      (accounts || [])
                        .filter(a => a?.category_id === category?.id)
                        .reduce((sum, a) => sum + parseFloat(a?.current_balance || 0), 0)
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Accounts Section */}
        <div className="section">
          <h3>Accounts</h3>
          <div className="accounts-table">
            <table>
              <thead>
                <tr>
                  <th>Account Name</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Opening Balance</th>
                  <th>Current Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(accounts || []).map((account) => (
                  <tr key={account?.id || Math.random()}>
                    <td>{account?.account_name || 'Unknown'}</td>
                    <td>{account?.category_name || 'Unknown'}</td>
                    <td>
                      <span className={`badge ${(account?.category_type || '').toLowerCase()}`}>
                        {account?.category_type || 'UNKNOWN'}
                      </span>
                    </td>
                    <td>{formatCurrency(account?.opening_balance || 0)}</td>
                    <td className="balance">{formatCurrency(account?.current_balance || 0)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-sm btn-primary" 
                          onClick={() => {
                            setSelectedAccount(account);
                            setShowTransactionModal(true);
                            setTransactionForm({...transactionForm, account_id: account?.id});
                          }}
                        >
                          <Plus className="w-3 h-3" /> Add Transaction
                        </button>
                        <button 
                          className="btn btn-sm btn-info" 
                          onClick={() => handleViewHistory(account)}
                        >
                          View History
                        </button>
                        <button 
                          className="btn btn-sm btn-warning" 
                          onClick={() => handleBalanceAdjust(account)}
                        >
                          Adjust Balance
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={() => handleViewBalanceHistory(account)}
                        >
                          Balance History
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEditAccount(account)}>
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteAccount(account?.id)}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Balance Adjustment History */}
      <div className="section">
        <h3>Balance Adjustment History</h3>
        <table className="history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Account</th>
              <th>Old Balance</th>
              <th>Change</th>
              <th>New Balance</th>
              <th>Type</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {balanceHistory.map((h, i) => (
              <tr key={i}>
                <td>{h.created_at}</td>
                <td>{h.account_name}</td>
                <td>{formatCurrency(h.old_balance)}</td>
                <td>{formatCurrency(h.change_amount)}</td>
                <td>{formatCurrency(h.new_balance)}</td>
                <td>{h.adjustment_type}</td>
                <td>{h.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {balanceHistory.length === 0 && (
          <p className="empty-state">No balance adjustments found</p>
        )}
      </div>

      {/* Transactions Modal */}
      {selectedAccount && (
        <div className="modal-overlay" onClick={() => setSelectedAccount(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transaction History - {selectedAccount.account_name}</h3>
              <button className="btn-icon" onClick={() => setSelectedAccount(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="modal-body">
              <div className="transactions-list">
                {transactions.length === 0 ? (
                  <p className="empty-state">No transactions found</p>
                ) : (
                  transactions.map((transaction) => (
                    <div key={transaction.id} className="transaction-item">
                      <div className="transaction-info">
                        <span className={`transaction-type ${transaction.type.toLowerCase()}`}>
                          {transaction.type === 'INCREASE' ? '+' : '-'} {formatCurrency(transaction.amount)}
                        </span>
                        <span className="transaction-date">{transaction.transaction_date}</span>
                      </div>
                      <div className="transaction-details">
                        <p>{transaction.note || 'No note'}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
              <button className="btn-icon" onClick={() => setShowCategoryModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCategorySubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Category Name</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                    placeholder="Enter category name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={categoryForm.type}
                    onChange={(e) => setCategoryForm({...categoryForm, type: e.target.value})}
                  >
                    <option value="ASSET">Asset</option>
                    <option value="LIABILITY">Liability</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                    placeholder="Enter category description (optional)"
                    rows="3"
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCategoryModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Account Modal */}
      {showAccountModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingAccount ? 'Edit Account' : 'Add Account'}</h3>
              <button className="btn-icon" onClick={() => setShowAccountModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAccountSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={accountForm.category_id}
                    onChange={(e) => setAccountForm({...accountForm, category_id: e.target.value})}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Account Name</label>
                  <input
                    type="text"
                    value={accountForm.account_name}
                    onChange={(e) => setAccountForm({...accountForm, account_name: e.target.value})}
                    placeholder="Enter account name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Opening Balance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={accountForm.opening_balance}
                    onChange={(e) => setAccountForm({...accountForm, opening_balance: e.target.value})}
                    placeholder="Enter opening balance (optional)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAccountModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : (editingAccount ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Transaction Modal */}
      {showTransactionModal && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Transaction</h3>
              <button className="btn-icon" onClick={() => setShowTransactionModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleTransactionSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Account</label>
                  <select
                    value={transactionForm.account_id}
                    onChange={(e) => setTransactionForm({...transactionForm, account_id: e.target.value})}
                    required
                  >
                    <option value="">Select Account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.account_name} ({account.category_name})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={transactionForm.type}
                    onChange={(e) => setTransactionForm({...transactionForm, type: e.target.value})}
                  >
                    <option value="INCREASE">Increase</option>
                    <option value="DECREASE">Decrease</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Note</label>
                  <textarea
                    value={transactionForm.note}
                    onChange={(e) => setTransactionForm({...transactionForm, note: e.target.value})}
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={transactionForm.transaction_date}
                    onChange={(e) => setTransactionForm({...transactionForm, transaction_date: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTransactionModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Transaction'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction History Modal */}
      {showHistoryModal && selectedAccount && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid #e9ecef' }}>
              <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '18px', fontWeight: '600' }}>Transaction History - {selectedAccount?.account_name || 'Unknown Account'}</h3>
              <button onClick={() => setShowHistoryModal(false)} style={{ padding: '6px', border: 'none', backgroundColor: 'transparent', color: '#6c757d', cursor: 'pointer', borderRadius: '4px' }}>
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              {(transactions || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  <p>No transactions found for this account.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600', color: '#2c3e50' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600', color: '#2c3e50' }}>Type</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600', color: '#2c3e50' }}>Amount</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa', fontWeight: '600', color: '#2c3e50' }}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(transactions || []).map((transaction) => (
                      <tr key={transaction?.id || Math.random()}>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e9ecef' }}>{transaction?.transaction_date || 'Unknown'}</td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e9ecef' }}>
                          <span style={{ 
                            padding: '4px 8px', 
                            borderRadius: '4px', 
                            fontSize: '12px', 
                            fontWeight: '500',
                            backgroundColor: transaction?.type === 'INCREASE' ? '#d4edda' : '#f8d7da',
                            color: transaction?.type === 'INCREASE' ? '#27ae60' : '#e74c3c'
                          }}>
                            {transaction?.type || 'UNKNOWN'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e9ecef' }}>{formatCurrency(transaction?.amount || 0)}</td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e9ecef' }}>{transaction?.note || 'No note'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Balance Adjustment Modal */}
      {showBalanceAdjustModal && selectedAccount && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid #e9ecef' }}>
              <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '18px', fontWeight: '600' }}>Adjust Balance - {selectedAccount?.account_name || 'Unknown Account'}</h3>
              <button onClick={() => setShowBalanceAdjustModal(false)} style={{ padding: '6px', border: 'none', backgroundColor: 'transparent', color: '#6c757d', cursor: 'pointer', borderRadius: '4px' }}>
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
            <form onSubmit={handleBalanceAdjustSubmit} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#495057' }}>Adjustment Type</label>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="adjustment_type"
                      value="manual_add"
                      checked={balanceAdjustForm.adjustment_type === 'manual_add'}
                      onChange={(e) => setBalanceAdjustForm({...balanceAdjustForm, adjustment_type: e.target.value})}
                      style={{ marginRight: '5px' }}
                    />
                    Add Balance
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="adjustment_type"
                      value="manual_subtract"
                      checked={balanceAdjustForm.adjustment_type === 'manual_subtract'}
                      onChange={(e) => setBalanceAdjustForm({...balanceAdjustForm, adjustment_type: e.target.value})}
                      style={{ marginRight: '5px' }}
                    />
                    Subtract Balance
                  </label>
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#495057' }}>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={balanceAdjustForm.amount}
                  onChange={(e) => setBalanceAdjustForm({...balanceAdjustForm, amount: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#495057' }}>Note</label>
                <textarea
                  value={balanceAdjustForm.note}
                  onChange={(e) => setBalanceAdjustForm({...balanceAdjustForm, note: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px', minHeight: '60px', resize: 'vertical' }}
                  placeholder="Reason for adjustment..."
                  required
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#495057' }}>Date</label>
                <input
                  type="date"
                  value={balanceAdjustForm.adjustment_date}
                  onChange={(e) => setBalanceAdjustForm({...balanceAdjustForm, adjustment_date: e.target.value})}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ced4da', borderRadius: '4px' }}
                  required
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowBalanceAdjustModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Adjusting...' : 'Adjust Balance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
